import React, { useState, useEffect } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import StripeAppTest from '../Stripe/StripeAppTest'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faCreditCard,
  faTag,
  faTicket,
} from '@fortawesome/pro-solid-svg-icons'
import { faXmark, faCircleCheck } from '@fortawesome/pro-regular-svg-icons'
import { useTranslation } from 'react-i18next'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'

const BRAND_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'

const SectionCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '14px',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(102, 126, 234, 0.12)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
}))

const getCurrency = () => {
  try {
    const stored = sessionStorage.getItem('currency')
    const parsed = stored ? JSON.parse(stored) : null
    return {
      rate: parsed?.currentRate ?? 1,
      devise: parsed?.devise ?? 'CHF',
    }
  } catch {
    return { rate: 1, devise: 'CHF' }
  }
}

const formatPrice = (amount) => {
  const { rate, devise } = getCurrency()
  return `${devise} ${((amount || 0) * rate).toFixed(2)}`
}

const PaymentModal = ({
  open,
  onClose,
  onSuccess,
  userId,
  userOffre,
  typeOffre,
  role,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [codePromo, setCodePromo] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorCodePromo, setErrorCodePromo] = useState(false)
  const [offreUtilisateur, setOffreUtilisateur] = useState(userOffre)

  useEffect(() => {
    if (open && userOffre) {
      setOffreUtilisateur(userOffre)
      setCodePromo('')
      setErrorCodePromo(false)
    }
  }, [open, userOffre])

  const isAtelier = typeOffre === 'atelier'
  const title = isAtelier
    ? offreUtilisateur?.atelier?.titre
    : offreUtilisateur?.offre?.titre
  const subtitle = isAtelier
    ? offreUtilisateur?.atelier?.sousTitre
    : offreUtilisateur?.offre?.sousTitre

  const applyPromo = async () => {
    if (!codePromo.trim()) return
    setLoading(true)
    setErrorCodePromo(false)
    try {
      const result = isAtelier
        ? await Api.ateliersUtilisateurs.edit(offreUtilisateur.id, {
            codePromoTexte: codePromo,
          })
        : await Api.offerUtilisateur.edit(
            offreUtilisateur.id,
            { codePromoTexte: codePromo },
            role
          )

      const handling = handlingErrors(result)
      if (handling.ok) {
        setOffreUtilisateur(handling.data)
        setCodePromo('')
      } else {
        setErrorCodePromo(true)
      }
    } catch {
      setErrorCodePromo(true)
    }
    setLoading(false)
  }

  const finalPrice = formatPrice(offreUtilisateur?.prixPromo)
  const originalPrice = formatPrice(offreUtilisateur?.prix)
  const stripePrice = (
    (offreUtilisateur?.prixPromo || 0) * getCurrency().rate
  ).toFixed(2)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      scroll={'paper'}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '20px',
          p: 0,
          m: fullScreen ? 0 : undefined,
          maxHeight: fullScreen ? '100%' : '90vh',
          overflow: 'hidden',
          ...(fullScreen && {
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          flexShrink: 0,
          background: BRAND_GRADIENT,
          color: '#fff',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FontAwesomeIcon icon={faCreditCard} fontSize={18} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={fullScreen ? 'subtitle1' : 'h6'}
              color="#fff"
              sx={{ fontWeight: 700, lineHeight: 1.25 }}
            >
              {isAtelier
                ? t('box.title.paimentAtelier')
                : t('box.title.paimentOffer')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.25, color: 'white' }}
            >
              {t('payment.secureCheckout', 'Paiement sécurisé')}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label={t('profile.close')}
          sx={{
            color: '#fff',
            flexShrink: 0,
            bgcolor: 'rgba(255,255,255,0.12)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          pt: { xs: '16px !important', sm: '32px !important' },
          flex: fullScreen ? 1 : undefined,
          overflow: 'auto',
          bgcolor: '#f8fafc',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Order summary */}
          <SectionCard sx={{ borderLeft: '4px solid #667eea' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background:
                    'linear-gradient(135deg, rgba(102, 126, 234, 0.14), rgba(118, 75, 162, 0.14))',
                  color: '#667eea',
                }}
              >
                <FontAwesomeIcon icon={faTag} fontSize={14} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="overline"
                  sx={{ color: '#667eea', fontWeight: 700, letterSpacing: 1 }}
                >
                  {t('payment.orderSummary', 'Récapitulatif')}
                </Typography>
                <Typography
                  variant={fullScreen ? 'subtitle1' : 'h6'}
                  sx={{
                    fontWeight: 700,
                    color: '#1f2937',
                    mt: 0.5,
                    wordBreak: 'break-word',
                  }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#6b7280', mt: 0.5, wordBreak: 'break-word' }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 2, borderColor: 'rgba(102, 126, 234, 0.1)' }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {offreUtilisateur?.codePromo && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: 'rgba(16, 185, 129, 0.06)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      textDecoration: 'line-through',
                    }}
                  >
                    {t('profile.price')}: {originalPrice}
                  </Typography>
                  <Chip
                    label="PROMO"
                    size="small"
                    sx={{
                      bgcolor: '#10b981',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'baseline' },
                  justifyContent: 'space-between',
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#6b7280', fontWeight: 500 }}
                >
                  {offreUtilisateur?.codePromo
                    ? t('profile.firstPrice')
                    : t('profile.price')}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: '#667eea',
                    lineHeight: 1.2,
                  }}
                >
                  {finalPrice}
                </Typography>
              </Box>
            </Box>
          </SectionCard>

          {/* Promo code */}
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FontAwesomeIcon icon={faTicket} color="#667eea" fontSize={16} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#374151' }}
              >
                {t('profile.haveYouCodePromo')}
              </Typography>
            </Box>

            {offreUtilisateur?.codePromo ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 1,
                  px: 2,
                  borderRadius: '10px',
                  bgcolor: 'rgba(16, 185, 129, 0.08)',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                }}
              >
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  color="#10b981"
                  fontSize={20}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#2e7d32' }}
                >
                  {t('profile.applyCodePromo')}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                <TextField
                  fullWidth
                  label={t('profile.enterCodePromo')}
                  placeholder={t('profile.enterCodePromo')}
                  variant="outlined"
                  value={codePromo}
                  error={errorCodePromo}
                  helperText={
                    errorCodePromo ? t('errorMessage.codePromo') : ' '
                  }
                  onChange={(e) => {
                    setCodePromo(e.target.value)
                    setErrorCodePromo(false)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyPromo()
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      bgcolor: '#fff',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  disabled={!codePromo.trim() || loading}
                  onClick={applyPromo}
                  sx={{
                    minWidth: { xs: '100%', sm: 120 },
                    py: 1.5,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    background: BRAND_GRADIENT,
                    boxShadow: '0 4px 10px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, #5a67d8 0%, #6b4c9a 100%)',
                    },
                    '&.Mui-disabled': {
                      background: '#e5e7eb',
                      color: '#9ca3af',
                    },
                  }}
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <FontAwesomeIcon icon={faCheck} />
                    )
                  }
                >
                  {t('payment.applyPromo', 'Appliquer')}
                </Button>
              </Box>
            )}
          </SectionCard>

          {/* Stripe payment */}
          <SectionCard>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: '#374151', mb: 0.5 }}
            >
              {t('payment.paymentMethod', 'Moyen de paiement')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#6b7280', display: 'block', mb: 2 }}
            >
              {t(
                'payment.stripeHint',
                'Carte bancaire — transaction sécurisée par Stripe'
              )}
            </Typography>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: '12px',
                bgcolor: '#f9fafb',
                border: '1px solid rgba(229, 231, 235, 0.9)',
                overflow: 'hidden',
                '& .MuiBox-root, & form': {
                  maxWidth: '100%',
                },
              }}
            >
              <StripeAppTest
                userId={userId}
                offreId={
                  isAtelier
                    ? offreUtilisateur?.atelier?.id
                    : offreUtilisateur?.id
                }
                offrePrice={stripePrice}
                offrePeriode={offreUtilisateur?.periode}
                onSuccess={onSuccess}
                onClose={onClose}
                type={typeOffre}
              />
            </Box>
          </SectionCard>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default PaymentModal
